import { apiClient } from './apiClient';
import { authService } from './authService';
import type {
  Profile,
  CreateProfileRequest,
  UpdateProfileRequest,
  ApiResponse,
  PaginatedResponse,
  ProfileFilters,
  PaginationParams
} from '../types/api';

class ProfileService {
  /**
   * Get all profiles for the current user
   */
  async getUserProfiles(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Profile>>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const userId = this.getCurrentUserId();
      const url = `/profiles/user/${userId}?${queryParams.toString()}`;
      console.log('🔍 ProfileService getUserProfiles - Making request to:', url);

      const response = await apiClient.get<PaginatedResponse<Profile>>(url);
      console.log('🔍 ProfileService getUserProfiles - Response:', response);
      return response;
    } catch (error: any) {
      console.error('❌ ProfileService getUserProfiles error:', error);
      throw error;
    }
  }

  /**
   * Get all profiles (admin/dev only)
   */
  async getAllProfiles(filters?: ProfileFilters, params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Profile>>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (filters?.userId) queryParams.append('userId', filters.userId);
      if (filters?.status !== undefined) queryParams.append('status', filters.status.toString());
      if (filters?.currency) queryParams.append('currency', filters.currency);
      if (filters?.search) queryParams.append('search', filters.search);
      
      const response = await apiClient.get<PaginatedResponse<Profile>>(
        `/profiles?${queryParams.toString()}`
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get a specific profile by ID
   */
  async getProfile(profileId: string): Promise<ApiResponse<Profile>> {
    try {
      const response = await apiClient.get<Profile>(`/profiles/${profileId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create a new profile
   */
  async createProfile(profileData: CreateProfileRequest): Promise<ApiResponse<Profile>> {
    try {
      const response = await apiClient.post<Profile>('/profiles', profileData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update an existing profile
   */
  async updateProfile(profileId: string, profileData: UpdateProfileRequest): Promise<ApiResponse<Profile>> {
    try {
      const response = await apiClient.put<Profile>(`/profiles/${profileId}`, profileData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Delete a profile
   */
  async deleteProfile(profileId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`/profiles/${profileId}`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Toggle profile status (activate/deactivate)
   */
  async toggleProfileStatus(profileId: string): Promise<ApiResponse<Profile>> {
    try {
      const response = await apiClient.patch<Profile>(`/profiles/${profileId}/toggle-status`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Regenerate API credentials for a profile
   */
  async regenerateApiCredentials(profileId: string): Promise<ApiResponse<Profile>> {
    try {
      const response = await apiClient.post<Profile>(`/profiles/${profileId}/regenerate-credentials`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get profile balance
   */
  async getProfileBalance(profileId: string): Promise<ApiResponse<{ balance: number; currency: string }>> {
    try {
      const response = await apiClient.get<{ balance: number; currency: string }>(`/profiles/${profileId}/balance`);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update profile balance (admin/dev only)
   */
  async updateProfileBalance(profileId: string, balance: number): Promise<ApiResponse<Profile>> {
    try {
      const response = await apiClient.patch<Profile>(`/profiles/${profileId}/balance`, { balance });
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get current user ID using authService
   */
  private getCurrentUserId(): string {
    try {
      const user = authService.getCurrentUser();
      console.log('🔍 ProfileService getCurrentUserId - User:', user);
      if (user) {
        const userId = user._id || user.id || '';
        console.log('🔍 ProfileService getCurrentUserId - Extracted ID:', userId);
        return userId;
      }
      throw new Error('User not found - please log in again');
    } catch (error) {
      console.error('❌ ProfileService getCurrentUserId error:', error);
      throw new Error('Failed to get current user ID - please log in again');
    }
  }

  /**
   * Validate profile data before submission
   */
  validateProfileData(data: CreateProfileRequest | UpdateProfileRequest): string[] {
    const errors: string[] = [];

    if ('accno' in data && (!data.accno || data.accno.trim().length === 0)) {
      errors.push('Account number is required');
    }

    if ('accname' in data && (!data.accname || data.accname.trim().length === 0)) {
      errors.push('Account name is required');
    }
    
    if ('accbank' in data && (!data.accbank || data.accbank.trim().length === 0)) {
      errors.push('Bank is required');
    }

    if ('callbackurl' in data && data.callbackurl && !this.isValidUrl(data.callbackurl)) {
      errors.push('Callback URL must be a valid URL');
    }

    return errors;
  }

  /**
   * Validate URL format
   */
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Format currency display
   */
  formatCurrency(amount: number, currency: string): string {
    const currencyMap: Record<string, string> = {
      myr: 'MYR',
      usd: 'USD',
      thb: 'THB',
      sgd: 'SGD'
    };

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyMap[currency.toLowerCase()] || 'MYR'
    }).format(amount);
  }

  /**
   * Get bank display name
   */
  getBankDisplayName(bankCode: string): string {
    const bankMap: Record<string, string> = {
      maybank: 'Maybank',
      cimb: 'CIMB Bank',
      publicbank: 'Public Bank',
      rhb: 'RHB Bank',
      hongleong: 'Hong Leong Bank',
      ambank: 'AmBank',
      uob: 'UOB Bank',
      ocbc: 'OCBC Bank',
      hsbc: 'HSBC Bank',
      standardchartered: 'Standard Chartered',
      citibank: 'Citibank',
      bankislam: 'Bank Islam',
      bankrakyat: 'Bank Rakyat',
      bsn: 'Bank Simpanan Nasional'
    };

    return bankMap[bankCode.toLowerCase()] || bankCode;
  }

  /**
   * Get available banks list
   */
  getAvailableBanks(): Array<{ code: string; name: string }> {
    return [
      { code: 'maybank', name: 'Maybank' },
      { code: 'cimb', name: 'CIMB Bank' },
      { code: 'publicbank', name: 'Public Bank' },
      { code: 'rhb', name: 'RHB Bank' },
      { code: 'hongleong', name: 'Hong Leong Bank' },
      { code: 'ambank', name: 'AmBank' },
      { code: 'uob', name: 'UOB Bank' },
      { code: 'ocbc', name: 'OCBC Bank' },
      { code: 'hsbc', name: 'HSBC Bank' },
      { code: 'standardchartered', name: 'Standard Chartered' },
      { code: 'citibank', name: 'Citibank' },
      { code: 'bankislam', name: 'Bank Islam' },
      { code: 'bankrakyat', name: 'Bank Rakyat' },
      { code: 'bsn', name: 'Bank Simpanan Nasional' }
    ];
  }

  /**
   * Get available currencies
   */
  getAvailableCurrencies(): Array<{ code: string; name: string; symbol: string }> {
    return [
      { code: 'myr', name: 'Malaysian Ringgit', symbol: 'RM' },
      { code: 'usd', name: 'US Dollar', symbol: '$' },
      { code: 'sgd', name: 'Singapore Dollar', symbol: 'S$' },
      { code: 'thb', name: 'Thai Baht', symbol: '฿' }
    ];
  }
}

// Create and export a singleton instance
export const profileService = new ProfileService();
export default profileService;
